import React, { useEffect, useState } from 'react';
import { S } from './Controls.styles';
import { ControlsTable } from './components/ControlsTable';
import { ControlsLayout } from '../../components/ControlsLayout';
import { ControlsSortDropdown } from './components/ControlsSortDropdown';
import { ControlsFilters } from './components/ControlsFilters';
import { Loading } from '../../../../components/Loading';
import { Error } from '../../../../components/Error';
import { useGetControls } from '../../../../traits/api/controls/useGetControls';
import type { SortControls } from '../../../../traits/api/controls/useGetControls';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { saveControlsListSearch } from '../../utils/controlsListNavigation';

export const Controls: React.FC = () => {
  const { state } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? 0);
  const setPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };
  const isCompleted = state === 'open' ? false : true;
  const [filterByProject, setFilterByProject] = useState('');
  const [filterByClient, setFilterByClient] = useState('');
  const [sort, setSort] = useState<SortControls>('date_DESC');

  useEffect(() => {
    if (!state) return;
    saveControlsListSearch(state, searchParams.toString());
  }, [state, searchParams]);

  const handleFilterByClient = (value: string) => {
    setFilterByClient(value);
    if (page !== 0) setPage(0);
  };

  const handleFilterByProject = (value: string) => {
    setFilterByProject(value);
    if (page !== 0) setPage(0);
  };

  const controls = useGetControls(
    page,
    sort,
    isCompleted,
    filterByProject.trim() || undefined,
    filterByClient.trim() || undefined
  );

  const hasActiveFilters =
    filterByClient.trim().length > 0 || filterByProject.trim().length > 0;

  return (
    <ControlsLayout
      isCompleted={isCompleted}
      setIsCompleted={(completed) => {
        if (completed) {
          navigate('/kontroller/closed');
        } else {
          navigate('/kontroller/open');
        }
      }}
    >
      <S.ControlsContainer>
        <div>
          <S.Margin $spacing="xs" />

          <S.ControlsHeader>
            <S.B3 $color="darkGray">
              {controls.isSuccess || controls.isPreviousData
                ? `${controls.count} ${
                    isCompleted ? 'avslutade' : 'pågående'
                  } kontroller`
                : `${isCompleted ? 'Avslutade' : 'Pågående'} kontroller`}
            </S.B3>

            <S.HeaderActions>
              <ControlsFilters
                filterByClient={filterByClient}
                filterByProject={filterByProject}
                setFilterByClient={handleFilterByClient}
                setFilterByProject={handleFilterByProject}
              />
              <ControlsSortDropdown sort={sort} setSort={setSort} />
            </S.HeaderActions>
          </S.ControlsHeader>

          <S.Margin $spacing="m" />

          {controls.isLoading && !controls.isPreviousData && <Loading />}

          {controls.isError && <Error refetch={controls.refetch} />}

          {(controls.isSuccess || controls.isPreviousData) &&
            controls.data &&
            controls.data.length === 0 && (
              <S.EmptyState>
                <S.H5 $weight={500}>
                  {hasActiveFilters
                    ? 'Inga kontroller matchade filtret'
                    : `Det finns inga ${
                        isCompleted ? 'avslutade' : 'pågående'
                      } kontroller`}
                </S.H5>
                {hasActiveFilters && (
                  <S.B2 $color="darkGray">
                    Prova att ändra eller rensa sökningen.
                  </S.B2>
                )}
              </S.EmptyState>
            )}

          {(controls.isSuccess || controls.isPreviousData) &&
            !!controls.data?.length && (
              <ControlsTable
                key={sort}
                data={controls.data}
                page={page}
                setPage={setPage}
                numberOfPages={Math.ceil(controls.count / 20)}
              />
            )}
        </div>
      </S.ControlsContainer>
    </ControlsLayout>
  );
};
